import React, { useCallback, useMemo, useState } from 'react';
import { Container, Sprite, Text } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import { bettingPartStore } from '../../../store/bettingPartStore';
import { textureChangeFrame } from '../../../core/helpers/textureChangeFrame';
import { getGameImages } from '../../../core/helpers/getGameImages';
import { gameDrawer } from '../../../services/gameDrawer';
import { gameService } from '../../../services/gameService';
import { gameStore } from '../../../store/gameStore';
import { isMobile } from '../../../core/helpers/isMobile';
import { freeBetStore } from '../../../store/freeBetStore';
import { isoStandard } from '../../../core/helpers/isoStandard';
import { abbrNum } from '../../../core/helpers/abbrNumber';
import { applicationStore } from '../../../store/applicationStore';
import { userInfoStore } from '../../../store/userInfoStore';

export const BetSection = observer(({ x = 0, y = 0, quickBetMargin = 10 }) => {
  const { isSpinStart } = gameStore;
  const {
    quickBetsByPage,
    selectedQuickBet,
    isShowQuickBetsListPopup,
    setSelectedQuickBet,
    isAutoBetStart,
  } = bettingPartStore;
  const { isShowLicenseMode, isDemo, isSkipSpinAfterBetChange } = applicationStore;

  const [isPressed, setIsPressed] = useState(false);
  const { quickBet, quickBetHover, quickBetPressed, quickBetSelected, quickBetDisabled } =
    useMemo(() => {
      return getGameImages();
    }, []);

  const handlePointerMove = useCallback(
    (e) => {
      const { value } = e.target;
      if (value === selectedQuickBet || isPressed || isSpinStart) return;
      textureChangeFrame(e.target.children[0], quickBetHover);
    },
    [selectedQuickBet, isPressed, quickBetHover, isSpinStart],
  );

  const handlePointerOut = useCallback(
    (e) => {
      const { value } = e.target;
      if (value === selectedQuickBet || isPressed || isSpinStart) return;
      textureChangeFrame(e.target.children[0], quickBet);
    },
    [selectedQuickBet, quickBet, isPressed, isSpinStart],
  );

  const handlePointerDown = useCallback(
    (e) => {
      const { value } = e.target;
      if (value === selectedQuickBet || isSpinStart) return;
      setIsPressed(true);
      textureChangeFrame(e.target.children[0], quickBetPressed);
    },
    [selectedQuickBet, quickBetPressed, isSpinStart],
  );

  const handleSpin = useCallback(
    (e) => {
      if (isSpinStart || isAutoBetStart) {
        gameDrawer.forceStopAnimation();
      } else {
        const { value } = e.target;
        setSelectedQuickBet(value);
        setIsPressed(false);
        textureChangeFrame(e.target.children[0], quickBetSelected);
        if (!isSkipSpinAfterBetChange) {
          gameService.doSpin();
        }
      }
    },
    [isSpinStart, isAutoBetStart, quickBetSelected, isSkipSpinAfterBetChange, setSelectedQuickBet],
  );

  const handlePointerUp = useCallback(
    (e) => {
      handleSpin(e);
    },
    [handleSpin],
  );

  const defaultTexture = useCallback(
    (el) => {
      if (el === selectedQuickBet) {
        return quickBetSelected;
      }
      if (isSpinStart || isAutoBetStart) {
        return quickBetDisabled;
      }
      return quickBet;
    },
    [isSpinStart, isAutoBetStart, selectedQuickBet, quickBet, quickBetDisabled, quickBetSelected],
  );

  const numberStandard = useCallback((number) => {
    return number >= 100000 ? abbrNum(number) : isoStandard(number, true);
  }, []);

  if (isShowQuickBetsListPopup) return null;

  return (
    <Container x={x} y={y} name="BottomCenter">
      {quickBetsByPage.map((el, index) => {
        return (
          <Container
            x={index * quickBetMargin}
            key={el}
            interactive={!isAutoBetStart && !freeBetStore.currentFreeBet}
            value={el}
            cursor="pointer"
            pointermove={handlePointerMove}
            pointerout={handlePointerOut}
            pointerdown={handlePointerDown}
            pointerup={handlePointerUp}
          >
            <Sprite texture={defaultTexture(el)} anchor={0.5} scale={isMobile() ? 0.77 : 0.5} />
            <Text
              text={`${
                isShowLicenseMode && !isDemo ? userInfoStore.currencyIcon : ''
              }${numberStandard(el)}`}
              style={{
                fontSize: isMobile() ? 20 : 22,
                fontWeight: 500,
                fontFamily: 'poppinsMedium',
                fill: '#FFFFFF',
              }}
              anchor={0.5}
              resolution={2}
              alpha={isSpinStart || isAutoBetStart ? 0.6 : 1}
            />
          </Container>
        );
      })}
      <Text
        x={isMobile() ? 220 : 275}
        y={isMobile() ? 40 : 35}
        visible={isShowLicenseMode && isDemo}
        text={applicationStore.canvasTranslationKeys.DEMO_MODE}
        style={{
          fontSize: 15,
          fontWeight: 500,
          fontFamily: 'poppinsMedium',
          fill: '#FFFFFF',
        }}
        anchor={0.5}
        resolution={2}
      />
    </Container>
  );
});
